import React, { useEffect, useState } from "react";
import * as XLSX from "xlsx";
import API_URLS from "../../summaryapi/apiUrls";

const StudentReports = () => {
  const [students, setStudents] = useState([]);
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const itemsPerPage = 10;

  const isAdmin = sessionStorage.getItem("isAdmin") === "true";
  const employeeId = sessionStorage.getItem("employeeId");

  useEffect(() => {
    fetchStudents();
  }, []);

  useEffect(() => {
    filterData();
  }, [search, fromDate, toDate, selectedEmployee, students]);

  const fetchStudents = async () => {
    setLoading(true);
    setError("");
    try {
      let response;
      if (isAdmin) {
        response = await fetch(API_URLS.getstudents.url);
      } else {
        const requestOptions = {
          method: API_URLS.getstudentsByEmployeeId.method,
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ employee_id: employeeId }),
        };
        response = await fetch(API_URLS.getstudentsByEmployeeId.url, requestOptions);
      }

      const result = await response.json();
      if (result.status) {
        setStudents(result.data);
        setFilteredStudents(result.data);
      } else {
        // setError(result.message || "Failed to fetch students");
      }
    } catch (err) {
      // setError("An error occurred while fetching students");
      // console.error("Fetch error:", err);
    } finally {
      setLoading(false);
    }
  };

  const filterData = () => {
    let data = [...students];

    if (search) {
      data = data.filter(
        (student) =>
          student.full_name.toLowerCase().includes(search.toLowerCase()) ||
          student.mobile_number.includes(search) ||
          student.parents_name.toLowerCase().includes(search.toLowerCase()) ||
          student.city.toLowerCase().includes(search.toLowerCase())
      );
    }

    if (fromDate) {
      data = data.filter((student) => {
        const studentDate = student.created_date.split(" ")[0];
        return studentDate >= fromDate;
      });
    }

    if (toDate) {
      data = data.filter((student) => {
        const studentDate = student.created_date.split(" ")[0];
        return studentDate <= toDate;
      });
    }

    if (isAdmin && selectedEmployee) {
      data = data.filter((student) => student.employee_name === selectedEmployee);
    }

    setFilteredStudents(data);
    setCurrentPage(1);
  };

  const exportToExcel = () => {
    const dataForExport = filteredStudents.map((student, index) => {
      const {
        student_id,
        employee_id,
        employee_name,
        is_active,
        is_interested,
        ...rest
      } = student;
  
      return {
        "S.No": index + 1,
        "Voluntary Name": employee_name || "",
        "Name": rest.full_name,
        "Gender": rest.gender,
        "Current Class": `Class ${rest.current_class}`,
        "Current School": rest.current_school_name,
        "Current Fee": rest.current_fee,
        "Joining Class": rest.joining_class,
        "Class Fee": rest.class_fee,
        "Parent Name": rest.parents_name,
        "Mobile": rest.mobile_number,
        "Alternate Mobile": rest.alternate_mobile_number || "",
        "City": rest.city,
        "Address": rest.address,
        "Income": rest.income || "",
        "Caste": rest.caste || "",
        "Student Aadhar": rest.student_aadhar || "",
        "Parent Aadhar": rest.parent_aadhar || "",
        "Remarks": rest.remarks || "",
        "Interested": is_interested === "1" ? "Yes" : "No",
        "Active Status": is_active === "1" ? "Active" : "Inactive",
        "Created Date": new Date(rest.created_date).toLocaleString("en-IN", {
          timeZone: "Asia/Kolkata",
        }),
      };
    });
  
    const ws = XLSX.utils.json_to_sheet(dataForExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Students");
    XLSX.writeFile(wb, "StudentReports.xlsx");
  };

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);

  const employeeNames = [...new Set(students.map((s) => s.employee_name))].filter(Boolean);

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-6 mx-auto">
      <h1 className="text-2xl font-bold text-center mb-6 text-gray-800">Students Report</h1>

      {loading && (
        <div className="mb-4 p-4 bg-blue-100 text-blue-700 rounded text-center">
          Loading student data...
        </div>
      )}

      {error && (
        <div className="mb-4 p-4 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4 mb-6">
        {isAdmin && (
          <select
            value={selectedEmployee}
            onChange={(e) => setSelectedEmployee(e.target.value)}
            className="border p-2 rounded-md w-full"
          >
            <option value="">All Voluntary</option>
            {employeeNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        )}
        <input
          type="date"
          value={fromDate}
          onChange={(e) => setFromDate(e.target.value)}
          className="border p-2 rounded-md w-full"
        />
        <input
          type="date"
          value={toDate}
          onChange={(e) => setToDate(e.target.value)}
          className="border p-2 rounded-md w-full"
        />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name or number"
          className="border p-2 rounded-md w-full"
        />

        <button
          onClick={exportToExcel}
          className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-md w-full"
        >
          Download Excel
        </button>
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 bg-white text-center border">
          <thead className="bg-gray-50 text-gray-700 text-sm">
            <tr>
              <th className="px-4 py-3 text-center">S.No</th>
              {isAdmin && <th className="px-4 py-3 text-center">Voluntary Name</th>}
              <th className="px-4 py-3 text-center">Name</th>
              <th className="px-4 py-3 text-center">Gender</th>
              <th className="px-4 py-3 text-center">Class</th>
              <th className="px-4 py-3 text-center">School</th>
              <th className="px-4 py-3 text-center">Current Fee</th>
              <th className="px-4 py-3 text-center">Parent</th>
              <th className="px-4 py-3 text-center">Mobile</th>
              <th className="px-4 py-3 text-center">City</th>
              <th className="px-4 py-3 text-center">Address</th>
              <th className="px-4 py-3 text-center">Income</th>
              <th className="px-4 py-3 text-center">Caste</th>
              <th className="px-4 py-3 text-center">Student Aadhar</th>
              <th className="px-4 py-3 text-center">Parent Aadhar</th>
              <th className="px-4 py-3 text-center">Remarks</th>
              <th className="px-4 py-3 text-center">Interested</th>
              <th className="px-4 py-3 text-center">Status</th>
              <th className="px-4 py-3 text-center">Created Date</th>
            </tr>
          </thead>
          <tbody className="text-gray-700 text-sm divide-y divide-gray-100">
            {currentItems.length > 0 ? (
              currentItems.map((student, index) => (
                <tr key={student.student_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3">{indexOfFirstItem + index + 1}</td>
                  {isAdmin && <td className="px-4 py-3">{student.employee_name}</td>}
                  <td className="px-4 py-3">{student.full_name}</td>
                  <td className="px-4 py-3">{student.gender}</td>
                  <td className="px-4 py-3">Class {student.current_class}</td>
                  <td className="px-4 py-3">{student.current_school_name}</td>
                  <td className="px-4 py-3">{student.current_fee}</td>
                  <td className="px-4 py-3">{student.parents_name}</td>
                  <td className="px-4 py-3">{student.mobile_number}</td>
                  <td className="px-4 py-3">{student.city}</td>
                  <td className="px-4 py-3">{student.address}</td>
                  <td className="px-4 py-3">{student.income}</td>
                  <td className="px-4 py-3">{student.caste}</td>
                  <td className="px-4 py-3">{student.student_aadhar}</td>
                  <td className="px-4 py-3">{student.parent_aadhar}</td>
                  <td className="px-4 py-3">{student.remarks}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      student.is_interested === "1" ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-800"
                    }`}>
                      {student.is_interested === "1" ? "Yes" : "No"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      student.is_active === "1" ? "bg-blue-100 text-blue-800" : "bg-red-100 text-red-800"
                    }`}>
                      {student.is_active === "1" ? "Active" : "Inactive"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    {new Date(student.created_date).toLocaleString("en-IN", {
                      timeZone: "Asia/Kolkata",
                    })}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={isAdmin ? "19" : "18"} className="text-center py-4 text-gray-500">
                  {loading ? "Loading..." : "No records found."}
                </td>
              </tr>
            )}
          </tbody>
          <tfoot>
            <tr>
              <td colSpan={isAdmin ? "19" : "18"} className="px-4 py-3 text-right text-sm text-gray-500">
                Showing {indexOfFirstItem + 1}-{Math.min(indexOfLastItem, filteredStudents.length)} of {filteredStudents.length} records
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex flex-wrap justify-center mt-6 gap-2">
          {Array.from({ length: totalPages }, (_, idx) => (
            <button
              key={idx}
              onClick={() => setCurrentPage(idx + 1)}
              className={`px-3 py-1 rounded-md border text-sm ${
                currentPage === idx + 1
                  ? "bg-blue-500 text-white"
                  : "bg-white text-gray-700 hover:bg-gray-100"
              }`}
            >
              {idx + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default StudentReports;